import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Eye, EyeOff, UserPlus, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import AuthPageShell from "../components/auth/AuthPageShell";
import {
  AuthTitle,
  AuthSub,
  Divider,
  Label,
  Input,
  PassWrap,
  TogglePass,
  Field,
  Submit,
  Footer,
  FooterLink,
  Err,
} from "../components/auth/authFormStyles";
import { cPath } from "../constants";
import { registerUser } from "../store/actions/authActions";
import useSEO from "../hooks/useSEO";

export default function RegisterPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loading, error, isAuthenticated } = useSelector((s) => s.auth);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate(cPath("dashboard"), { replace: true });
  }, [isAuthenticated, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !password) return;
    await dispatch(
      registerUser({
        name: name.trim(),
        email: email.trim(),
        password,
      })
    );
  };

  const seo = useSEO({ title: "Create Account", noindex: true, nofollow: true });

  return (
    <>
      {seo}
      <AuthPageShell heroHint={t("auth.register.heroHint")}>
      <Divider />
      <AuthTitle>{t("auth.register.title")}</AuthTitle>
      <AuthSub>{t("auth.register.subtitle")}</AuthSub>
      {error ? (
        <Err role="alert">
          <AlertCircle size={18} strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{error}</span>
        </Err>
      ) : null}
      <form onSubmit={onSubmit} noValidate>
        <Field>
          <Label htmlFor="reg-name">{t("auth.register.displayName")}</Label>
          <Input
            id="reg-name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("auth.register.displayNamePlaceholder")}
          />
        </Field>
        <Field>
          <Label htmlFor="reg-email">{t("auth.register.email")}</Label>
          <Input
            id="reg-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </Field>
        <Field>
          <Label htmlFor="reg-pass">{t("auth.register.password")}</Label>
          <PassWrap>
            <Input
              id="reg-pass"
              type={show ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("auth.register.choosePassword")}
              style={{ paddingRight: "2.75rem" }}
            />
            <TogglePass
              type="button"
              aria-label={show ? "Hide password" : "Show password"}
              onClick={() => setShow((v) => !v)}
            >
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </TogglePass>
          </PassWrap>
        </Field>
        <Submit type="submit" disabled={loading}>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
            <UserPlus size={18} strokeWidth={2} aria-hidden />
            {loading ? t("auth.register.submitting") : t("auth.register.submit")}
          </span>
        </Submit>
      </form>
      <Footer>
        {t("auth.register.alreadyRegistered")} <FooterLink to={cPath("signin")}>{t("auth.register.signInInstead")}</FooterLink>
      </Footer>
      </AuthPageShell>
    </>
  );
}
